class RococoTheme {
  constructor(root) {
    this.root = root;
    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.surveyHideTimer = null;
    this.surveyShowTimer = null;
    this.bindPalette();
    this.bindTracks();
    this.bindSurvey();
    const hasDoor = this.bindDoor();
    if (!hasDoor) this.queueSurvey(550);
    this.bindReveal();
  }

  bindPalette() {
    const options = this.root.querySelectorAll('[data-palette-option]');
    options.forEach((option) => {
      option.addEventListener('click', () => {
        const palette = option.dataset.paletteOption;
        this.root.dataset.palette = palette;
        options.forEach((item) => {
          const active = item === option;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-checked', String(active));
        });
      }, { signal: this.signal });
    });
  }

  bindTracks() {
    this.root.querySelectorAll('[data-track-prev], [data-track-next]').forEach((button) => {
      button.addEventListener('click', () => {
        const id = button.dataset.trackPrev || button.dataset.trackNext;
        const track = document.getElementById(id);
        if (!track) return;
        const card = track.firstElementChild;
        const distance = card ? card.getBoundingClientRect().width + 24 : track.clientWidth * 0.8;
        track.scrollBy({ left: button.hasAttribute('data-track-prev') ? -distance : distance, behavior: 'smooth' });
      }, { signal: this.signal });
    });
  }

  bindDoor() {
    const intro = this.root.querySelector('[data-door-intro]');
    const enter = this.root.querySelector('[data-door-enter]');
    if (!intro || !enter) return false;

    if (window.Shopify && window.Shopify.designMode) {
      intro.hidden = true;
      return false;
    }

    const open = () => {
      if (intro.classList.contains('is-opening')) return;
      intro.classList.add('is-opening');
      document.body.classList.remove('is-locked');
      window.setTimeout(() => intro.remove(), 950);
      this.queueSurvey(980);
    };

    document.body.classList.add('is-locked');
    enter.addEventListener('click', open, { signal: this.signal });
    intro.addEventListener('click', (event) => {
      if (event.target === intro) open();
    }, { signal: this.signal });
    intro.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') open();
    }, { signal: this.signal });
    window.setTimeout(() => enter.focus(), 100);
    return true;
  }

  bindSurvey() {
    this.survey = this.root.querySelector('[data-welcome-survey]');
    if (!this.survey) return;

    if (window.Shopify && window.Shopify.designMode) {
      this.survey.hidden = true;
      return;
    }

    const form = this.survey.querySelector('[data-survey-form]');
    const close = this.survey.querySelector('[data-survey-close]');
    const continueButton = this.survey.querySelector('[data-survey-continue]');
    const options = this.survey.querySelectorAll('input[type="radio"]');

    options.forEach((option) => {
      option.addEventListener('change', () => {
        continueButton.disabled = false;
        this.survey.querySelectorAll('.welcome-survey__option').forEach((label) => {
          label.classList.toggle('is-selected', label.contains(option));
        });
      }, { signal: this.signal });
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const selected = form.querySelector('input[type="radio"]:checked');
      if (!selected) return;
      this.rememberSurvey(selected.value);
      this.hideSurvey();
    }, { signal: this.signal });

    close.addEventListener('click', () => {
      this.rememberSurvey('dismissed');
      this.hideSurvey();
    }, { signal: this.signal });

    this.survey.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      this.rememberSurvey('dismissed');
      this.hideSurvey();
    }, { signal: this.signal });
  }

  queueSurvey(delay) {
    if (!this.survey || this.isSurveyRemembered()) return;
    window.clearTimeout(this.surveyShowTimer);
    this.surveyShowTimer = window.setTimeout(() => this.showSurvey(), delay);
  }

  showSurvey() {
    if (!this.survey || this.isSurveyRemembered()) return;
    this.lastFocusedElement = document.activeElement;
    this.survey.hidden = false;
    document.body.classList.add('is-locked');
    window.requestAnimationFrame(() => {
      this.survey.classList.add('is-visible');
      const close = this.survey.querySelector('[data-survey-close]');
      if (close) close.focus({ preventScroll: true });
    });
  }

  hideSurvey() {
    if (!this.survey) return;
    this.survey.classList.remove('is-visible');
    document.body.classList.remove('is-locked');
    window.clearTimeout(this.surveyHideTimer);
    this.surveyHideTimer = window.setTimeout(() => {
      if (this.survey) this.survey.hidden = true;
    }, 260);
    if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
      this.lastFocusedElement.focus({ preventScroll: true });
    }
  }

  isSurveyRemembered() {
    if (!this.survey) return true;
    try {
      return window.sessionStorage.getItem(this.survey.dataset.surveyStorageKey) === 'true';
    } catch (error) {
      return false;
    }
  }

  rememberSurvey(choice) {
    if (!this.survey) return;
    try {
      window.sessionStorage.setItem(this.survey.dataset.surveyStorageKey, 'true');
      window.sessionStorage.setItem(`${this.survey.dataset.surveyStorageKey}_choice`, choice);
    } catch (error) {
    }
  }

  bindReveal() {
    const items = this.root.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        this.observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px' });
    items.forEach((item) => this.observer.observe(item));
  }

  destroy() {
    this.abortController.abort();
    if (this.observer) this.observer.disconnect();
    window.clearTimeout(this.surveyHideTimer);
    window.clearTimeout(this.surveyShowTimer);
    document.body.classList.remove('is-locked');
  }
}

class StorefrontSection {
  constructor(root) {
    this.root = root;
    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.bindSorting();
    this.bindDetails();
    this.bindQuantity();
    this.bindGallery();
    this.bindProductZoom();
    this.bindVariants();
    this.bindProductSubtotal();
    this.bindStickyPurchaseBar();
  }

  bindSorting() {
    this.root.querySelectorAll('[data-collection-sort]').forEach((select) => {
      select.addEventListener('change', () => select.form.submit(), { signal: this.signal });
    });
  }

  bindDetails() {
    this.root.querySelectorAll('[data-details-close]').forEach((button) => {
      button.addEventListener('click', () => {
        const details = button.closest('details');
        if (details) details.removeAttribute('open');
      }, { signal: this.signal });
    });

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.root.querySelectorAll('[data-smooth-accordion] details').forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      const content = summary && summary.nextElementSibling;
      if (!summary || !content || reducedMotion) return;

      let animation = null;
      let contentAnimation = null;
      let isClosing = false;
      let isExpanding = false;

      const finish = (isOpen) => {
        details.open = isOpen;
        details.style.height = '';
        details.style.overflow = '';
        details.classList.remove('is-closing', 'is-expanding');
        if (contentAnimation) contentAnimation.cancel();
        animation = null;
        contentAnimation = null;
        isClosing = false;
        isExpanding = false;
      };

      const animate = (shouldOpen) => {
        const startHeight = `${details.getBoundingClientRect().height}px`;
        const contentStyle = window.getComputedStyle(content);
        const startOpacity = contentStyle.opacity;
        const startTransform = contentStyle.transform === 'none' ? 'translateY(0)' : contentStyle.transform;

        if (animation) animation.cancel();
        if (contentAnimation) contentAnimation.cancel();

        details.style.height = startHeight;
        details.style.overflow = 'hidden';
        details.open = true;
        isClosing = !shouldOpen;
        isExpanding = shouldOpen;
        details.classList.toggle('is-closing', isClosing);
        details.classList.toggle('is-expanding', isExpanding);

        const endHeight = `${summary.offsetHeight + (shouldOpen ? content.offsetHeight : 0)}px`;
        const duration = shouldOpen ? 440 : 340;
        const easing = shouldOpen ? 'cubic-bezier(.22, 1, .36, 1)' : 'cubic-bezier(.4, 0, .2, 1)';

        animation = details.animate({ height: [startHeight, endHeight] }, { duration, easing, fill: 'both' });
        contentAnimation = content.animate([
          { opacity: startOpacity, transform: startTransform },
          { opacity: shouldOpen ? 1 : 0, transform: shouldOpen ? 'translateY(0)' : 'translateY(-8px)' }
        ], { duration: shouldOpen ? 360 : 220, easing, fill: 'both' });
        animation.onfinish = () => finish(shouldOpen);
      };

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        animate(isClosing || !details.open);
      }, { signal: this.signal });
    });
  }

  normalizeQuantity(input, value = input.value) {
    const min = Number(input.min) || 1;
    const step = Number(input.step) || 1;
    const max = input.max === '' ? Infinity : Number(input.max);
    const number = Number(value);
    const aligned = min + Math.round((Math.max(min, Number.isFinite(number) ? number : min) - min) / step) * step;
    return Math.max(min, Math.min(aligned, Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : Infinity));
  }

  bindProductSubtotal() {
    const input = this.root.querySelector('.product-form input[name="quantity"]');
    const subtotal = this.root.querySelector('[data-product-subtotal]');
    if (!input || !subtotal) return;
    const update = (normalize) => {
      const quantity = this.normalizeQuantity(input);
      if (normalize) input.value = String(quantity);
      const cents = Number(this.root.dataset.priceCents) || 0;
      subtotal.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.root.dataset.currency, currencyDisplay: 'code'
      }).format(cents * quantity / 100);
      this.root.querySelectorAll('[data-quantity-change]').forEach(button => {
        const change = Number(button.dataset.quantityChange);
        button.disabled = change < 0 ? quantity <= Number(input.min) : input.max !== '' && quantity >= Number(input.max);
      });
      this.queueStickyPurchaseSync();
    };
    input.addEventListener('input', () => update(false), { signal: this.signal });
    input.addEventListener('change', () => update(true), { signal: this.signal });
    input.form.addEventListener('submit', () => update(true), { signal: this.signal });
    update(true);
  }

  bindQuantity() {
    this.root.querySelectorAll('[data-quantity-change]').forEach((button) => {
      button.addEventListener('click', () => {
        const selector = button.closest('.quantity-selector');
        const input = selector && selector.querySelector('input[type="number"]');
        if (!input) return;
        const minimum = Number(input.min) || 1;
        const change = Number(button.dataset.quantityChange) || 0;
        input.value = String(this.normalizeQuantity(input, (Number(input.value) || minimum) + change * (Number(input.step) || 1)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }, { signal: this.signal });
    });
  }

  bindStickyPurchaseBar() {
    this.stickyBuy = this.root.querySelector('[data-product-sticky-buy]');
    this.purchaseActions = this.root.querySelector('[data-product-purchase-actions]');
    this.productSubmit = this.root.querySelector('[data-product-submit]');
    this.productQuantity = this.root.querySelector('.product-form input[name="quantity"]');
    if (!this.stickyBuy || !this.purchaseActions || !this.productSubmit || !this.productQuantity) return;

    this.stickySubmit = this.stickyBuy.querySelector('[data-product-sticky-submit]');
    this.stickyQuantity = this.stickyBuy.querySelector('[data-product-sticky-quantity]');
    this.stickyMedia = this.stickyBuy.querySelector('[data-product-sticky-media]');
    this.stickyMediaKey = null;
    this.stickyQuantityButtons = Array.from(this.stickyBuy.querySelectorAll('[data-product-sticky-quantity-change]'));

    if (this.stickySubmit) {
      this.stickySubmit.addEventListener('click', () => {
        if (!this.productSubmit.disabled) this.productSubmit.click();
      }, { signal: this.signal });
    }

    this.stickyQuantityButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const minimum = Number(this.productQuantity.min) || 1;
        const maximum = Number(this.productQuantity.max) || Number.POSITIVE_INFINITY;
        const change = Number(button.dataset.productStickyQuantityChange) || 0;
        const current = Number(this.productQuantity.value) || minimum;
        this.productQuantity.value = String(this.normalizeQuantity(this.productQuantity, current + change * (Number(this.productQuantity.step) || 1)));
        this.productQuantity.dispatchEvent(new Event('change', { bubbles: true }));
      }, { signal: this.signal });
    });

    this.productQuantity.addEventListener('input', () => this.queueStickyPurchaseSync(), { signal: this.signal });
    this.productQuantity.addEventListener('change', () => this.queueStickyPurchaseSync(), { signal: this.signal });
    window.addEventListener('scroll', () => this.queueStickyPurchaseSync(), { passive: true, signal: this.signal });
    window.addEventListener('resize', () => this.queueStickyPurchaseSync(), { passive: true, signal: this.signal });

    if ('IntersectionObserver' in window) {
      this.purchaseObserver = new IntersectionObserver(() => this.queueStickyPurchaseSync(), {
        threshold: [0, 0.01, 1]
      });
      this.purchaseObserver.observe(this.productSubmit);
    }

    this.queueStickyPurchaseSync();
  }

  queueStickyPurchaseSync() {
    if (!this.stickyBuy || this.stickyBuyFrame) return;
    this.stickyBuyFrame = window.requestAnimationFrame(() => {
      this.stickyBuyFrame = null;
      this.syncStickyPurchaseBar();
    });
  }

  syncStickyPurchaseBar() {
    if (!this.stickyBuy || !this.purchaseActions || !this.productSubmit || !this.productQuantity) return;

    const priceSource = this.root.querySelector('[data-product-subtotal]') || this.root.querySelector('[data-product-price]');
    const priceTarget = this.stickyBuy.querySelector('[data-product-sticky-price]');
    const submitTextSource = this.productSubmit.querySelector('[data-product-submit-text]');
    const submitTextTarget = this.stickyBuy.querySelector('[data-product-sticky-submit-text]');
    const currentQuantity = Math.max(Number(this.productQuantity.min) || 1, Number(this.productQuantity.value) || 1);
    const maximumQuantity = Number(this.productQuantity.max) || Number.POSITIVE_INFINITY;

    if (priceSource && priceTarget) priceTarget.textContent = priceSource.textContent.trim();
    if (submitTextSource && submitTextTarget) submitTextTarget.textContent = submitTextSource.textContent.trim();
    const activeThumb = this.root.querySelector('.product-gallery__thumb.is-active');
    const activeVisual = activeThumb && activeThumb.querySelector('img, svg');
    const mediaKey = activeThumb ? (activeThumb.dataset.mediaId || 'placeholder') : '';
    if (this.stickyMedia && activeVisual && mediaKey !== this.stickyMediaKey) {
      const clone = activeVisual.cloneNode(true);
      clone.removeAttribute('id');
      clone.setAttribute('class', 'product-sticky-buy__image');
      clone.setAttribute('aria-hidden', 'true');
      if (clone.tagName === 'IMG') {
        clone.setAttribute('loading', 'eager');
        clone.setAttribute('sizes', '58px');
      }
      this.stickyMedia.replaceChildren(clone);
      this.stickyMediaKey = mediaKey;
    }
    if (this.stickyQuantity) this.stickyQuantity.textContent = String(currentQuantity);
    if (this.stickySubmit) this.stickySubmit.disabled = this.productSubmit.disabled;
    this.stickyQuantityButtons.forEach((button) => {
      const change = Number(button.dataset.productStickyQuantityChange) || 0;
      button.disabled = change < 0 ? currentQuantity <= (Number(this.productQuantity.min) || 1) : currentQuantity >= maximumQuantity;
    });

    const stickyHeader = document.querySelector('.site-header--sticky');
    const headerRect = stickyHeader && stickyHeader.getBoundingClientRect();
    const headerBottom = headerRect ? Math.max(0, headerRect.bottom) : 0;
    const stickyTop = Math.round(headerBottom + 16);
    this.root.style.setProperty('--product-sticky-top', `${stickyTop}px`);

    const submitRect = this.productSubmit.getBoundingClientRect();
    const sectionRect = this.root.getBoundingClientRect();
    const barHeight = this.stickyBuy.offsetHeight || 82;
    const actionsHavePassed = submitRect.top <= headerBottom;
    const sectionIsActive = sectionRect.bottom > barHeight;
    const shouldShow = actionsHavePassed && sectionIsActive;

    this.stickyBuy.classList.toggle('is-visible', shouldShow);
    this.stickyBuy.setAttribute('aria-hidden', String(!shouldShow));
    this.stickyBuy.inert = !shouldShow;
    document.body.classList.toggle('has-product-sticky-buy', shouldShow);
  }

  bindGallery() {
    this.galleryButtons = Array.from(this.root.querySelectorAll('[data-gallery-thumb]'));
    this.galleryButtons.forEach((button) => {
      button.addEventListener('click', () => this.activateMedia(button.dataset.mediaId), { signal: this.signal });
    });
    const active = this.galleryButtons.find((button) => button.classList.contains('is-active'));
    if (active) this.updateGalleryCounter(active);
  }

  bindProductZoom() {
    this.zoomStage = this.root.querySelector('[data-product-zoom-stage]');
    this.zoomLens = this.root.querySelector('[data-product-zoom-lens]');
    this.zoomPane = this.root.querySelector('[data-product-zoom-pane]');
    this.zoomSurface = this.root.querySelector('[data-product-zoom-surface]');
    this.zoomGallery = this.zoomStage && this.zoomStage.closest('[data-product-gallery]');
    const desktopPointer = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 1180px)');
    if (!this.zoomStage || !this.zoomLens || !this.zoomPane || !this.zoomSurface || !desktopPointer.matches) return;

    const scheduleZoom = (event) => {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      this.zoomPointer = { x: event.clientX, y: event.clientY };
      if (this.zoomFrame) return;
      this.zoomFrame = window.requestAnimationFrame(() => {
        this.zoomFrame = null;
        this.updateProductZoom();
      });
    };

    this.zoomStage.addEventListener('pointerenter', scheduleZoom, { signal: this.signal });
    this.zoomStage.addEventListener('pointermove', scheduleZoom, { signal: this.signal });
    this.zoomStage.addEventListener('pointerleave', () => this.hideProductZoom(), { signal: this.signal });
  }

  prepareProductZoomVisual(source, visual) {
    if (this.zoomSource === source && this.zoomVisual) return;
    this.zoomSurface.replaceChildren();
    const clone = visual.cloneNode(true);
    clone.setAttribute('class', 'product-gallery__zoom-image');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');
    clone.removeAttribute('srcset');
    clone.removeAttribute('sizes');
    clone.removeAttribute('loading');
    if (clone.tagName === 'IMG' && source.dataset.zoomSrc) clone.setAttribute('src', source.dataset.zoomSrc);
    this.zoomSurface.appendChild(clone);
    this.zoomSource = source;
    this.zoomVisual = clone;
  }

  updateProductZoom() {
    const source = this.zoomStage.querySelector('[data-product-zoom-source]:not([hidden])');
    const visual = source && source.querySelector('.product-gallery__image');
    if (!source || !visual || !this.zoomPointer) {
      this.hideProductZoom();
      return;
    }

    const sourceRect = visual.getBoundingClientRect();
    const pointerX = this.zoomPointer.x - sourceRect.left;
    const pointerY = this.zoomPointer.y - sourceRect.top;
    if (pointerX < 0 || pointerY < 0 || pointerX > sourceRect.width || pointerY > sourceRect.height) {
      this.hideProductZoom();
      return;
    }

    this.prepareProductZoomVisual(source, visual);
    this.zoomStage.classList.add('is-zooming');
    if (this.zoomGallery) this.zoomGallery.classList.add('is-zooming');
    const stageRect = this.zoomStage.getBoundingClientRect();
    const paneRect = this.zoomPane.getBoundingClientRect();
    const zoomLevel = Number(this.zoomStage.dataset.productZoomLevel) || 2.3;
    const lensWidth = Math.min(sourceRect.width, paneRect.width / zoomLevel);
    const lensHeight = Math.min(sourceRect.height, paneRect.height / zoomLevel);
    const centerX = Math.max(lensWidth / 2, Math.min(sourceRect.width - lensWidth / 2, pointerX));
    const centerY = Math.max(lensHeight / 2, Math.min(sourceRect.height - lensHeight / 2, pointerY));
    const lensX = sourceRect.left - stageRect.left + centerX - lensWidth / 2;
    const lensY = sourceRect.top - stageRect.top + centerY - lensHeight / 2;
    const zoomX = paneRect.width / 2 - centerX * zoomLevel;
    const zoomY = paneRect.height / 2 - centerY * zoomLevel;

    this.zoomLens.style.width = `${lensWidth}px`;
    this.zoomLens.style.height = `${lensHeight}px`;
    this.zoomLens.style.transform = `translate3d(${lensX}px, ${lensY}px, 0)`;
    this.zoomVisual.style.width = `${sourceRect.width * zoomLevel}px`;
    this.zoomVisual.style.height = `${sourceRect.height * zoomLevel}px`;
    this.zoomVisual.style.transform = `translate3d(${zoomX}px, ${zoomY}px, 0)`;
  }

  hideProductZoom() {
    if (!this.zoomStage) return;
    this.zoomStage.classList.remove('is-zooming');
    if (this.zoomGallery) this.zoomGallery.classList.remove('is-zooming');
    this.zoomPointer = null;
  }

  activateMedia(mediaId) {
    if (!mediaId) return;
    this.hideProductZoom();
    this.root.querySelectorAll('[data-product-media]').forEach((media) => {
      media.hidden = media.dataset.mediaId !== String(mediaId);
    });
    this.galleryButtons.forEach((button) => {
      const active = button.dataset.mediaId === String(mediaId);
      button.classList.toggle('is-active', active);
      if (active) {
        button.setAttribute('aria-current', 'true');
        this.updateGalleryCounter(button);
      } else {
        button.removeAttribute('aria-current');
      }
    });
    this.queueStickyPurchaseSync();
  }

  updateGalleryCounter(activeButton) {
    const counter = this.root.querySelector('[data-gallery-current]');
    if (!counter) return;
    const index = this.galleryButtons.indexOf(activeButton);
    counter.textContent = String(index + 1);
  }

  bindVariants() {
    this.root.querySelectorAll('[data-product-variant-select]').forEach((select) => {
      select.addEventListener('change', () => this.updateVariant(select), { signal: this.signal });
    });
  }

  updateVariant(select) {
    const option = select.options[select.selectedIndex];
    const productSection = select.closest('[data-product-section]');
    if (!option || !productSection) return;

    const price = productSection.querySelector('[data-product-price]');
    const comparePrice = productSection.querySelector('[data-product-compare-price]');
    const saleBadge = productSection.querySelector('[data-product-sale-badge]');
    const sku = productSection.querySelector('[data-product-sku]');
    const submit = productSection.querySelector('[data-product-submit]');
    const submitText = productSection.querySelector('[data-product-submit-text]');
    const available = option.dataset.available === 'true';
    const hasComparePrice = Boolean(option.dataset.comparePrice);

    if (price) price.textContent = option.dataset.price;
    productSection.dataset.priceCents = option.dataset.priceCents;
    const quantity = productSection.querySelector('.product-form input[name="quantity"]');
    if (quantity) {
      quantity.min = option.dataset.quantityMin || '1';
      quantity.step = option.dataset.quantityStep || '1';
      if (option.dataset.quantityMax) quantity.max = option.dataset.quantityMax;
      else quantity.removeAttribute('max');
      quantity.value = String(this.normalizeQuantity(quantity));
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (comparePrice) {
      comparePrice.textContent = option.dataset.comparePrice;
      comparePrice.hidden = !hasComparePrice;
    }
    if (saleBadge) saleBadge.hidden = !hasComparePrice;
    if (sku) sku.textContent = option.dataset.sku;
    if (submit) submit.disabled = !available;
    if (submitText && submit) submitText.textContent = available ? submit.dataset.addLabel : submit.dataset.soldLabel;
    if (option.dataset.mediaId) this.activateMedia(option.dataset.mediaId);
    this.queueStickyPurchaseSync();

    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    window.history.replaceState({}, '', url);
  }

  destroy() {
    if (this.zoomFrame) window.cancelAnimationFrame(this.zoomFrame);
    if (this.stickyBuyFrame) window.cancelAnimationFrame(this.stickyBuyFrame);
    if (this.purchaseObserver) this.purchaseObserver.disconnect();
    if (this.stickyBuy) {
      document.body.classList.remove('has-product-sticky-buy');
      this.root.style.removeProperty('--product-sticky-top');
    }
    this.hideProductZoom();
    this.abortController.abort();
  }
}

const instances = new WeakMap();
const storefrontInstances = new WeakMap();

function initializeRococo(scope = document) {
  scope.querySelectorAll('[data-rococo-section]').forEach((root) => {
    if (instances.has(root)) return;
    instances.set(root, new RococoTheme(root));
  });
}

function initializeStorefront(scope = document) {
  scope.querySelectorAll('[data-storefront-section]').forEach((root) => {
    if (storefrontInstances.has(root)) return;
    storefrontInstances.set(root, new StorefrontSection(root));
  });

  scope.querySelectorAll('.site-header__mobile').forEach((details) => {
    if (details.dataset.bound === 'true') return;
    details.dataset.bound = 'true';
    details.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => details.removeAttribute('open')));
  });
}

document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeRococo();
    initializeStorefront();
  });
} else {
  initializeRococo();
  initializeStorefront();
}

document.addEventListener('shopify:section:load', (event) => {
  initializeRococo(event.target);
  initializeStorefront(event.target);
});
document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.querySelector('[data-rococo-section]');
  const instance = root && instances.get(root);
  if (instance) {
    instance.destroy();
    instances.delete(root);
  }
  const storefrontRoot = event.target.querySelector('[data-storefront-section]');
  const storefrontInstance = storefrontRoot && storefrontInstances.get(storefrontRoot);
  if (storefrontInstance) {
    storefrontInstance.destroy();
    storefrontInstances.delete(storefrontRoot);
  }
});
